<template>
  <div
    ref="rootRef"
    :class="[
      'ui-search-select',
      sizeClass,
      {
        'ui-search-select--disabled': disabled,
        'ui-search-select--open': open,
        'ui-search-select--multi': multiple,
      },
    ]"
  >
    <!--
      Trigger button. Renders the current value (single) or chip row
      (multi). Click toggles the popover; keyboard opens/navigates.
    -->
    <button
      ref="triggerRef"
      type="button"
      class="ui-search-select__trigger"
      :id="id"
      :disabled="disabled"
      :aria-haspopup="'listbox'"
      :aria-expanded="open"
      :aria-controls="listboxId"
      :aria-label="ariaLabel"
      :aria-invalid="invalid || undefined"
      role="combobox"
      @click="toggle"
      @keydown="onTriggerKeydown"
    >
      <!-- Multi mode: chip row (or placeholder when empty). -->
      <span v-if="multiple" class="ui-search-select__chips">
        <template v-if="selectedOptions.length">
          <span
            v-for="opt in selectedOptions"
            :key="String(opt[valueField])"
            class="ui-search-select__chip"
          >
            <span class="ui-search-select__chip-label">{{ opt[textField] }}</span>
            <span
              v-if="!disabled"
              class="ui-search-select__chip-remove"
              role="button"
              :aria-label="`Remove ${opt[textField]}`"
              @click.stop="remove(opt)"
              @keydown.enter.stop="remove(opt)"
              @keydown.space.stop.prevent="remove(opt)"
              tabindex="0"
            >&times;</span>
          </span>
        </template>
        <span v-else class="ui-search-select__placeholder">{{ placeholder }}</span>
      </span>

      <!-- Single mode: text or placeholder. -->
      <span v-else class="ui-search-select__value">
        <template v-if="selectedOption">{{ selectedOption[textField] }}</template>
        <span v-else class="ui-search-select__placeholder">{{ placeholder }}</span>
      </span>

      <!--
        Clearable × — single mode only, only when a value is set.
        Clicking emits `null` and a `clear` event so callers can hook
        side-effects (e.g. ViewHeader's clearNode() which also busts
        caches and replaces the route).
      -->
      <span
        v-if="clearable && !multiple && selectedOption && !disabled"
        class="ui-search-select__clear"
        role="button"
        :aria-label="clearAriaLabel"
        tabindex="0"
        @click.stop="clear"
        @keydown.enter.stop="clear"
        @keydown.space.stop.prevent="clear"
      >&times;</span>

      <span class="ui-search-select__chevron" aria-hidden="true">
        <!-- Inline chevron-down so we don't depend on FontAwesome here. -->
        <svg
          viewBox="0 0 20 20"
          fill="none"
          width="14"
          height="14"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.5"
            d="m6 8 4 4 4-4"
          />
        </svg>
      </span>
    </button>

    <!--
      Popover. Inline (not teleported) so it inherits the host's
      stacking context — sidesteps the `appendToBody` positioning bug
      that bit us with vue3-treeselect inside b-modal.
    -->
    <div
      v-if="open"
      class="ui-search-select__popover"
      role="presentation"
    >
      <div v-if="searchable" class="ui-search-select__search">
        <input
          ref="searchInputRef"
          v-model="search"
          type="text"
          class="ui-search-select__search-input"
          :placeholder="searchPlaceholder"
          :aria-controls="listboxId"
          :aria-activedescendant="activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined"
          autocomplete="off"
          @keydown="onSearchKeydown"
        >
      </div>
      <ul
        ref="optionsListRef"
        :id="listboxId"
        class="ui-search-select__options"
        role="listbox"
        :aria-multiselectable="multiple || undefined"
      >
        <li
          v-for="(opt, i) in filteredOptions"
          :id="`${listboxId}-${i}`"
          :key="String(opt[valueField])"
          :class="[
            'ui-search-select__option',
            {
              'ui-search-select__option--active': i === activeIndex,
              'ui-search-select__option--selected': isSelected(opt),
            },
          ]"
          role="option"
          :aria-selected="isSelected(opt)"
          @click="select(opt)"
          @mouseenter="activeIndex = i"
        >
          {{ opt[textField] }}
        </li>
        <li v-if="!filteredOptions.length" class="ui-search-select__no-results">
          {{ noResultsText }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
  /**
   * UiSearchSelect — searchable single/multi select primitive.
   *
   * Owned in-house (built from scratch, not a wrapper) to keep Slice 6's
   * lock-in surface minimal. Replaces `vue-search-select`'s `ModelSelect`
   * (single) and `MultiSelect`/`MultiListSelect` (multi). Visually parity
   * with UiSelect — gray-300 border, primary-600 focus ring, sm/md/lg
   * heights — and the same `valueField`/`textField` prop names so it
   * slots into the existing design system without surprising callers.
   *
   * v-model semantics:
   *   single mode → scalar value (or null)
   *   multi  mode → array of values
   *
   * Both modes pull labels from `options` via `textField`. Custom shapes
   * (e.g. CountryFilter's `id`/`name` records) are supported via
   * `valueField` / `textField`. Migrating away from `vue-search-select`'s
   * "objects in / objects out" pattern in favour of plain values is a
   * deliberate simplification — see the migration commits for the data-
   * shape adjustments at each call site.
   *
   * Usage:
   *   <UiSearchSelect v-model="industry" :options="industries" />
   *   <UiSearchSelect v-model="countries" :options="countries" multiple
   *     value-field="id" text-field="name" />
   *
   * The popover renders inline (no Teleport). This trades a tiny risk
   * of clipping by `overflow: hidden` parents for a guarantee that the
   * popover sits in the host's stacking context — i.e. inside a modal
   * the popover stays inside the modal. We've already paid for the
   * teleport-positioning bug once with vue3-treeselect; not paying again.
   */
  import { ref, computed, nextTick, onBeforeUnmount, watch } from 'vue'

  defineOptions({ name: 'UiSearchSelect', inheritAttrs: false })

  type Value = string | number
  type Option = Record<string, any>

  interface Props {
    modelValue?: Value | Value[] | null
    options?: Option[]
    multiple?: boolean
    searchable?: boolean
    /** Single mode: render a clear (×) button when a value is set. */
    clearable?: boolean
    clearAriaLabel?: string
    valueField?: string
    textField?: string
    placeholder?: string
    searchPlaceholder?: string
    disabled?: boolean
    size?: 'sm' | 'md' | 'lg'
    id?: string
    ariaLabel?: string
    invalid?: boolean
    noResultsText?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
    options: () => [],
    multiple: false,
    searchable: true,
    clearable: false,
    clearAriaLabel: 'Clear selection',
    valueField: 'value',
    textField: 'text',
    placeholder: 'Select…',
    searchPlaceholder: 'Search…',
    disabled: false,
    size: 'md',
    id: undefined,
    ariaLabel: undefined,
    invalid: false,
    noResultsText: 'No results',
  })

  const emit = defineEmits<{
    'update:modelValue': [value: Value | Value[] | null]
    'change': [value: Value | Value[] | null]
    'clear': []
  }>()

  // Auto-incrementing id so each instance's listbox/option ids are unique
  // for `aria-controls` / `aria-activedescendant` wiring.
  let _idCounter = 0
  const listboxId = `ui-search-select-${++_idCounter}`

  const rootRef = ref<HTMLDivElement | null>(null)
  const triggerRef = ref<HTMLButtonElement | null>(null)
  const searchInputRef = ref<HTMLInputElement | null>(null)
  const optionsListRef = ref<HTMLUListElement | null>(null)

  const open = ref(false)
  const search = ref('')
  const activeIndex = ref(-1)

  const sizeClass = computed(() => `ui-search-select--${props.size}`)

  // Normalised view of the model. `null` and `undefined` collapse to an
  // empty array in multi mode so downstream `.includes()` is safe.
  const selectedValues = computed<Value[]>(() => {
    if (props.multiple) {
      return Array.isArray(props.modelValue) ? props.modelValue : []
    }
    return props.modelValue == null ? [] : [props.modelValue as Value]
  })

  function isSelected (opt: Option): boolean {
    return selectedValues.value.includes(opt[props.valueField])
  }

  // Single mode: the option that matches the scalar v-model.
  const selectedOption = computed<Option | undefined>(() =>
    props.options.find(o => o[props.valueField] === props.modelValue)
  )

  // Multi mode: list of selected options preserving caller order.
  const selectedOptions = computed<Option[]>(() => {
    if (!props.multiple) return []
    return selectedValues.value
      .map(v => props.options.find(o => o[props.valueField] === v))
      .filter((o): o is Option => Boolean(o))
  })

  // Case-insensitive substring match on the textField. Lifts heavy
  // search to a separate primitive (e.g. fuzzy match) only if a caller
  // ever asks — current consumers all want plain "contains".
  const filteredOptions = computed<Option[]>(() => {
    if (!search.value.trim()) return props.options
    const q = search.value.toLowerCase()
    return props.options.filter(opt =>
      String(opt[props.textField] ?? '').toLowerCase().includes(q)
    )
  })

  function toggle () {
    if (props.disabled) return
    if (open.value) close()
    else doOpen()
  }

  function doOpen () {
    open.value = true
    search.value = ''
    activeIndex.value = -1
    nextTick(() => {
      // Auto-focus the search input so typing starts filtering immediately.
      if (props.searchable) searchInputRef.value?.focus()
      // Land the active row on the first selected option (if any) so the
      // user gets visual confirmation of "where they were".
      const firstSelected = filteredOptions.value.findIndex(isSelected)
      if (firstSelected >= 0) activeIndex.value = firstSelected
    })
  }

  function close () {
    open.value = false
    search.value = ''
    activeIndex.value = -1
  }

  function select (opt: Option) {
    if (props.multiple) {
      const v = opt[props.valueField]
      const next = isSelected(opt)
        ? selectedValues.value.filter(x => x !== v)
        : [...selectedValues.value, v]
      emit('update:modelValue', next)
      emit('change', next)
      // Multi keeps the popover open so the user can pick several in a row.
      // Reset the search so they can keep typing for the next pick.
      search.value = ''
      nextTick(() => searchInputRef.value?.focus())
    } else {
      const v = opt[props.valueField] as Value
      emit('update:modelValue', v)
      emit('change', v)
      close()
      triggerRef.value?.focus()
    }
  }

  function remove (opt: Option) {
    if (!props.multiple) return
    const v = opt[props.valueField]
    const next = selectedValues.value.filter(x => x !== v)
    emit('update:modelValue', next)
    emit('change', next)
  }

  function clear () {
    if (props.disabled) return
    emit('update:modelValue', props.multiple ? [] : null)
    emit('change', props.multiple ? [] : null)
    emit('clear')
  }

  function onTriggerKeydown (ev: KeyboardEvent) {
    if (props.disabled) return
    switch (ev.key) {
      case 'Enter':
      case ' ':
      case 'ArrowDown':
        ev.preventDefault()
        if (!open.value) doOpen()
        break
      case 'Escape':
        if (open.value) {
          ev.preventDefault()
          close()
        }
        break
    }
  }

  function onSearchKeydown (ev: KeyboardEvent) {
    switch (ev.key) {
      case 'ArrowDown':
        ev.preventDefault()
        moveActive(1)
        break
      case 'ArrowUp':
        ev.preventDefault()
        moveActive(-1)
        break
      case 'Enter':
        ev.preventDefault()
        if (activeIndex.value >= 0 && filteredOptions.value[activeIndex.value]) {
          select(filteredOptions.value[activeIndex.value])
        }
        break
      case 'Escape':
        ev.preventDefault()
        close()
        triggerRef.value?.focus()
        break
      case 'Tab':
        // Let Tab move focus naturally; closing the popover is the only
        // correct behaviour since the user is leaving the control.
        close()
        break
    }
  }

  function moveActive (delta: number) {
    const len = filteredOptions.value.length
    if (!len) return
    let next = activeIndex.value + delta
    if (next < 0) next = len - 1
    if (next >= len) next = 0
    activeIndex.value = next
    // Keep the active row in view when navigating with the keyboard.
    nextTick(() => {
      const list = optionsListRef.value
      const el = list?.children[next] as HTMLElement | undefined
      if (!el || !list) return
      const elTop = el.offsetTop
      const elBottom = elTop + el.offsetHeight
      if (elTop < list.scrollTop) list.scrollTop = elTop
      else if (elBottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = elBottom - list.clientHeight
      }
    })
  }

  // Click-outside detection — listen at document level only while open
  // so we don't pay the listener cost on every page mousedown.
  function onDocumentMousedown (ev: MouseEvent) {
    if (!open.value) return
    const root = rootRef.value
    if (root && !root.contains(ev.target as Node)) close()
  }

  watch(open, (isOpen) => {
    if (isOpen) {
      document.addEventListener('mousedown', onDocumentMousedown)
    } else {
      document.removeEventListener('mousedown', onDocumentMousedown)
    }
  })

  onBeforeUnmount(() => {
    document.removeEventListener('mousedown', onDocumentMousedown)
  })

  defineExpose({
    /** Programmatically open the popover (e.g. from a parent toolbar). */
    open: doOpen,
    close,
    /** Imperative focus on the trigger. */
    focus: () => triggerRef.value?.focus(),
  })
</script>

<style scoped>
  .ui-search-select {
    position: relative;
    display: flex;
    align-items: stretch;
    width: 100%;
  }

  /* Trigger — visually identical to UiSelect's chrome (gray-300 border,
     primary-600 focus ring) so the two primitives sit alongside each
     other in forms without a chrome mismatch. */
  .ui-search-select__trigger {
    flex: 1 1 auto;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    background-color: #fff;
    color: #111827;
    font-size: 0.875rem;
    line-height: 1.25rem;
    text-align: left;
    cursor: pointer;
    transition: border-color 150ms ease, box-shadow 150ms ease;
  }
  .ui-search-select__trigger:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }
  .ui-search-select__trigger:disabled {
    background-color: #f9fafb;
    color: #6b7280;
    cursor: not-allowed;
  }
  .ui-search-select__trigger[aria-invalid="true"] {
    border-color: #dc2626;
  }
  .ui-search-select__trigger[aria-invalid="true"]:focus {
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15);
  }

  /* Sizes — heights, padding, and font-sizes match UiInput so a
     UiSearchSelect placed next to a UiInput / UiSelect in the same
     row sits on the same baseline. */
  .ui-search-select--sm .ui-search-select__trigger {
    min-height: 1.875rem;               /* 30px */
    padding: 0.25rem 0.625rem;
    font-size: 0.8125rem;
  }
  .ui-search-select--md .ui-search-select__trigger {
    min-height: 2.25rem;                /* 36px */
    padding: 0.375rem 0.75rem;
  }
  .ui-search-select--lg .ui-search-select__trigger {
    min-height: 2.75rem;                /* 44px */
    padding: 0.625rem 1rem;
    font-size: 1rem;
  }

  /* Open state — slight visual cue without changing the focus ring. */
  .ui-search-select--open .ui-search-select__trigger {
    border-color: #2563eb;
  }

  .ui-search-select__value {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    /* Explicit text-align so we win against any host-context inheritance
       (e.g. ViewHeader sits inside `.nav-item { text-align: center }`
       which would otherwise push the trigger label to the centre). */
    text-align: left;
  }
  .ui-search-select__placeholder {
    color: #9ca3af;
  }

  .ui-search-select__chevron {
    display: inline-flex;
    align-items: center;
    color: #6b7280;
    flex: 0 0 auto;
    transition: transform 150ms ease;
  }
  .ui-search-select--open .ui-search-select__chevron {
    transform: rotate(180deg);
  }

  /* Clear (×) — sits between the value and the chevron in single mode
     when `clearable` is set. Subtle by default, brightens on hover so
     it's clearly affordant without competing with the chevron. */
  .ui-search-select__clear {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.125rem;
    height: 1.125rem;
    border-radius: 0.25rem;
    color: #9ca3af;                     /* gray-400 */
    cursor: pointer;
    user-select: none;
    line-height: 1;
    font-size: 1rem;
  }
  .ui-search-select__clear:hover,
  .ui-search-select__clear:focus {
    background-color: #f3f4f6;          /* gray-100 */
    color: #6b7280;                     /* gray-500 */
    outline: none;
  }

  /* Multi mode — chip row. Inline-flex with wrap so chips reflow when
     the trigger is narrow. */
  .ui-search-select__chips {
    flex: 1 1 auto;
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    min-width: 0;
    text-align: left;
  }
  .ui-search-select__chip {
    display: inline-flex;
    align-items: center;
    gap: 0.125rem;
    background-color: #eff6ff;          /* primary-50 — matches treeselect chip */
    color: #1d4ed8;                     /* primary-700 */
    border-radius: 0.25rem;
    padding: 0.0625rem 0.375rem;
    font-size: 0.8125rem;
    line-height: 1.125rem;
  }
  .ui-search-select__chip-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ui-search-select__chip-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
    height: 1rem;
    border-radius: 0.125rem;
    cursor: pointer;
    user-select: none;
    color: #1d4ed8;
  }
  .ui-search-select__chip-remove:hover,
  .ui-search-select__chip-remove:focus {
    background-color: #dbeafe;
    color: #1e3a8a;
    outline: none;
  }

  /* Popover — sits absolutely below the trigger, full width of the
     control. z-index high enough to clear BVN modals (1050). The
     popover is a sibling of the trigger (not a descendant), so the
     trigger's `text-align: left` doesn't reach it; explicit alignment
     here so popover contents render correctly when the host context
     forces `text-align: center` on us (e.g. ViewHeader's `.nav-item`). */
  .ui-search-select__popover {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 4px;
    background-color: #fff;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    z-index: 1060;
    overflow: hidden;
    text-align: left;
  }

  .ui-search-select__search {
    padding: 0.5rem;
    border-bottom: 1px solid #e5e7eb;
  }
  .ui-search-select__search-input {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
  .ui-search-select__search-input:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }

  .ui-search-select__options {
    list-style: none;
    margin: 0;
    padding: 0.25rem 0;
    max-height: 16rem;                  /* ~256px */
    overflow-y: auto;
  }
  .ui-search-select__option {
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: #111827;
    cursor: pointer;
    user-select: none;
  }
  .ui-search-select__option--active {
    background-color: #f3f4f6;
  }
  .ui-search-select__option--selected {
    background-color: #eff6ff;
    color: #1d4ed8;
    font-weight: 500;
  }
  .ui-search-select__option--selected.ui-search-select__option--active {
    background-color: #dbeafe;
  }
  .ui-search-select__no-results {
    padding: 0.5rem 0.75rem;
    color: #9ca3af;
    font-style: italic;
    font-size: 0.875rem;
  }

  .ui-search-select--disabled { opacity: 0.85; }
</style>
